import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@Src/app/hooks';
import { AppRouteURL, AppRouteURLwithChilds } from '@Constants/app-route-url.constant';
import { getUserDetails } from '@Features/UserProfile/Services/UserProfile.sevice';
import { USE_MOCK_DATA, getMockUserDetails } from '@Src/mocks/subscriptionMockData';
import { checkInstituteExamLimit } from "@Features/Institute/services/Institute.service";
import { showSpinner } from '@Src/app/reducers/globalSlice';
import { toast } from 'react-hot-toast';
import { getPreviousOpenStateTest } from '@Src/features/TestDetails/services/TestDetails.service';

export const useSubscriptionNavigation = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const loginData = useAppSelector((state: any) => state.login);
    const isLoggedIn = loginData?.isLoggedIn;

    const [showNoSubDialog, setShowNoSubDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [dialogHeading, setDialogHeading] = useState('');
    const [showSubscribeButton, setShowSubscribeButton] = useState(false);
    const [pendingTest, setPendingTest] = useState<any>(null);
    const [showPendingTestDialog, setShowPendingTestDialog] = useState(false);
    const [pendingTargetUrl, setPendingTargetUrl] = useState<string | null>(null);
    const [pendingOnSuccess, setPendingOnSuccess] = useState<(() => void) | undefined>(undefined);
    const [pendingStateTransfer, setPendingStateTransfer] = useState<any>(undefined);

    const checkSubscriptionAndNavigate = async (targetUrl: string | null, onSuccess?: () => void, stateTransfer?: any, skipPendingCheck: boolean = false) => {
        if (!isLoggedIn) {
            navigate(AppRouteURL.AUTH + AppRouteURLwithChilds.AUTH.childrens.LOGIN.path);
            return;
        }


        try {
            // Fetch all subscription data from API for real-time accuracy
            const response: any = USE_MOCK_DATA
                ? await getMockUserDetails()
                : await getUserDetails();

            if (response?.ResponseType === 'SUCCESS' && response?.data?.[0]) {
                const userDetails = response.data[0];
                const {
                    last_student_subscription_rel_id,
                    Student_sub_EndDate,
                    Institute_sub_PlanName,
                    Institute_sub_EndDate,
                    Student_sub_BalanceNumberOfMocktests,
                    Institute_sub_BalanceNumberOfMocktests
                } = userDetails;

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                let hasActiveSub = false;
                let subTargetUrl = targetUrl;

                // Both are null - no subscription
                if (!last_student_subscription_rel_id && !Institute_sub_PlanName) {
                    const isTestSeriesPage = targetUrl ? targetUrl.includes('/testseries') : false;
                    setDialogMessage(isTestSeriesPage ? 'Do you want to subscribe?' : 'You have no active subscription. Please subscribe to continue.');
                    setDialogHeading('');
                    setShowSubscribeButton(true);
                    setShowNoSubDialog(true);
                    return;
                }

                // Student subscription exists
                if (last_student_subscription_rel_id && Student_sub_EndDate) {
                    const endDate = new Date(Student_sub_EndDate);
                    endDate.setHours(0, 0, 0, 0);
                    if (endDate >= today) {
                        // Check if student has tests remaining
                        if (Student_sub_BalanceNumberOfMocktests === 0) {
                            setDialogMessage('Your test limit is over, you need to subscribe now.');
                            setDialogHeading('No test left');
                            setShowSubscribeButton(false);
                            setShowNoSubDialog(true);
                            return;
                        }
                        hasActiveSub = true;
                    } else {
                        setDialogMessage('You have no active subscription. Please subscribe to continue.');
                        setDialogHeading('');
                        setShowSubscribeButton(true);
                        setShowNoSubDialog(true);
                        // navigate(AppRouteURL.SUBSCRIPTION);
                        return;
                    }
                }

                // Institute subscription exists
                if (!hasActiveSub && Institute_sub_PlanName && Institute_sub_EndDate) {
                    const instEndDate = new Date(Institute_sub_EndDate);
                    instEndDate.setHours(0, 0, 0, 0);
                    if (instEndDate >= today) {
                        // Check if institute has tests remaining
                        if (Institute_sub_BalanceNumberOfMocktests === 0 || Institute_sub_BalanceNumberOfMocktests < 0) {
                            setDialogMessage('Your test limit is over, Please contact your institute\'s administrator');
                            setDialogHeading('No test left');
                            setShowSubscribeButton(false);
                            setShowNoSubDialog(true);
                            return;
                        }
                        hasActiveSub = true;
                    } else {
                        setDialogMessage('You have no active subscription. Please contact your institute\'s administrator.');
                        setDialogHeading('');
                        setShowSubscribeButton(false);
                        setShowNoSubDialog(true);
                        return;
                    }
                }

                if (hasActiveSub) {
                    // Check for pending test before navigating
                    const doNavigate = async () => {
                        // if (!skipPendingCheck) {
                        //     try {
                        //         const prevTestRes: any = await getPreviousOpenStateTest();
                        //         if (prevTestRes?.ResponseType === 'SUCCESS' && Object.keys(prevTestRes?.data).length > 0) {
                        //             setPendingTest(prevTestRes.data);
                        //             setPendingTargetUrl(targetUrl);
                        //             setPendingOnSuccess(onSuccess ? () => onSuccess : undefined);
                        //             setPendingStateTransfer(stateTransfer);
                        //             setShowPendingTestDialog(true);
                        //             return;
                        //         }
                        //     } catch (_) { }
                        // }
                        // No pending test or skip check — proceed normally
                        if (onSuccess) {
                            onSuccess();
                        } else if (subTargetUrl) {
                            if (stateTransfer) {
                                navigate(subTargetUrl, { state: stateTransfer });
                            } else {
                                navigate(subTargetUrl);
                            }
                        }
                    };

                    if (userDetails.InstituteId) {
                        dispatch(showSpinner(true));
                        const limitRes = await checkInstituteExamLimit();
                        dispatch(showSpinner(false));

                        if (limitRes.ResponseType === 'SUCCESS' && limitRes.data?.[0]) {
                            const limitData = limitRes.data[0];
                            if (limitData.allowed) {
                                await doNavigate();
                            } else {
                                setDialogHeading(limitData.message === "Subscription expired" ? "Subscription expired" : 'Exam Limit Reached');
                                setDialogMessage(limitData.message === "Subscription expired" ? "You don't have any active subscription. Please contact your institute's administrator." : "You have reached your exam limit. Please contact your institute's administrator.");
                                setShowSubscribeButton(false);
                                setShowNoSubDialog(true);
                            }
                        } else {
                            toast.error('Unable to verify exam limit. Please try again.');
                        }
                    } else {
                        await doNavigate();
                    }
                    return;
                }
            } else {
                // If API fails, show error
                setDialogMessage('Unable to verify subscription. Please try again.');
                setDialogHeading('');
                setShowSubscribeButton(false);
                setShowNoSubDialog(true);
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
            setDialogMessage('Unable to verify subscription. Please try again.');
            setDialogHeading('');
            setShowSubscribeButton(false);
            setShowNoSubDialog(true);
        }
    };

    const handleSubscribe = () => {
        setShowNoSubDialog(false);
        if (showSubscribeButton) {
            localStorage.setItem('redirectAfterSubscription', window.location.pathname);
        }
        navigate(AppRouteURL.SUBSCRIPTION);
    };

    const handleContinuePendingTest = () => {
        setShowPendingTestDialog(false);
        navigate(`${AppRouteURL.DASHBOARD}${AppRouteURL.TEST_ACTIVITY}`, { state: { activeTab: 0 } });
    };

    const handleIgnorePendingTest = () => {
        setShowPendingTestDialog(false);
        setPendingTest(null);
        if (pendingOnSuccess) {
            pendingOnSuccess();
        } else if (pendingTargetUrl) {
            if (pendingStateTransfer) {
                navigate(pendingTargetUrl, { state: pendingStateTransfer });
            } else {
                navigate(pendingTargetUrl);
            }
        }
    };

    const pendingTestDialogProps = {
        visible: showPendingTestDialog,
        onHide: () => setShowPendingTestDialog(false),
        pendingTest,
        onContinue: handleContinuePendingTest,
        onIgnore: handleIgnorePendingTest,
    };

    const subscriptionDialogProps = {
        visible: showNoSubDialog,
        onHide: () => setShowNoSubDialog(false),
        onSubscribe: handleSubscribe,
        message: dialogMessage,
        heading: dialogHeading,
        showSubscribeButton: showSubscribeButton,
        isTestSeries: dialogMessage === 'Do you want to subscribe?'
    };

    return {
        checkSubscriptionAndNavigate,
        subscriptionDialogProps,
        pendingTestDialogProps,
    };
};
